import { useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { Add as AddIcon, CategoryOutlined } from "@mui/icons-material";
import AdministrarAreasDialog from "../../components/tema/AdministrarAreasDialog";
import CatalogoRemoto from "../../components/tema/CatalogoRemoto";
import CatalogoTemas from "../../components/tema/CatalogoTemas";
import CrearTemaDialog from "../../components/tema/CrearTemaDialog";
import TemaTarjeta from "../../components/tema/TemaTarjeta";
import { useTemasApp } from "../../queries/useTemas";
import { useAuthStore } from "../../stores/authStore";
import { TemaApp } from "../../types/tema";

/**
 * "Temas": los temas de la persona y el catálogo completo. La sección propia es
 * extensible a cualquier tipo de usuario: si tiene un único tema (p. ej. el
 * estudiante con su tesis) se muestra como tarjeta; si tiene más de uno (p. ej.
 * un docente con varias propuestas) se muestra como lista. Cada tema lleva a su
 * ficha, desde donde se postula.
 */
export default function Temas() {
  const { user } = useAuthStore();
  // Los temas propios (su tesis y/o los que ofrece como guía o coguía) llegan
  // completos: son pocos y la sección los filtra en memoria. El catálogo, que
  // puede tener cientos, se pide aparte y lo filtra y pagina el servidor.
  const { data, isLoading, isError } = useTemasApp({ mios: 1, page_size: 100 });
  const misTemas = useMemo(() => data?.results ?? [], [data]);
  const [crearAbierto, setCrearAbierto] = useState(false);
  const [areasAbierto, setAreasAbierto] = useState(false);
  // Estudiante puro: propone temas (elige guía); docente/coordinador los crea/ofrece.
  const esProponente = !!user?.es_estudiante && !user?.es_coordinador_global && !user?.es_docente;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Temas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.puede_crear_tema
              ? "Tus temas propuestos y el resto del catálogo."
              : "El tema en el que trabajas y el catálogo de titulación."}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          {user?.es_coordinador_global && (
            <Button variant="outlined" startIcon={<CategoryOutlined />} onClick={() => setAreasAbierto(true)}>
              Administrar áreas
            </Button>
          )}
          {user?.puede_crear_tema && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCrearAbierto(true)}>
              {esProponente ? "Proponer tema" : "Crear tema"}
            </Button>
          )}
        </Stack>
      </Stack>

      {user?.puede_crear_tema && <CrearTemaDialog open={crearAbierto} onClose={() => setCrearAbierto(false)} />}
      {areasAbierto && <AdministrarAreasDialog onClose={() => setAreasAbierto(false)} />}

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudieron cargar los temas.</Alert>}

      {!isLoading && !isError && (
        <Stack spacing={4}>
          <MisTemasSection temas={misTemas} puedeCrear={!!user?.puede_crear_tema} />
          <Seccion title={user?.puede_crear_tema ? "Otros temas" : "Catálogo de temas"}>
            <CatalogoRemoto />
          </Seccion>
        </Stack>
      )}
    </Box>
  );
}

/**
 * Sección de los temas propios: 1 tema → tarjeta; más de uno → lista.
 *
 * Lo cerrado sigue siendo suyo pero estorba, así que se esconde tras "Ver todo"
 * mientras quede algo vigente. Son dos cosas:
 *
 * - Las propuestas que el guía rechazó (el tema queda en `rechazado`).
 * - Los temas donde su postulación fue rechazada, pero **solo** cuando su
 *   situación ya está resuelta: aceptaron una postulación suya y ese es el tema
 *   de su proceso. Aceptar un tema rechaza el resto automáticamente, y esos
 *   quedaban acá como si todavía fueran algo.
 *
 * Si TODO está cerrado se muestra igual — esconderlo diría "aún no has propuesto
 * temas", que es falso.
 */
function MisTemasSection({ temas, puedeCrear }: { temas: TemaApp[]; puedeCrear: boolean }) {
  const [verTodo, setVerTodo] = useState(false);

  // El tema de su proceso, y que además le aceptaron: recién ahí las postulaciones
  // rechazadas dejan de ser información útil.
  const resuelto = temas.some((t) => t.es_mi_tema && t.mi_postulacion?.estado === "aceptada");
  const cerrado = (t: TemaApp) =>
    t.estado_codigo === "rechazado" || (resuelto && t.mi_postulacion?.estado === "rechazada");

  const vigentes = temas.filter((t) => !cerrado(t));
  const rechazados = temas.length - vigentes.length;
  const visibles = verTodo || vigentes.length === 0 ? temas : vigentes;
  const hayToggle = rechazados > 0 && vigentes.length > 0;

  const toggle = hayToggle && (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
      <Button size="small" variant="text" onClick={() => setVerTodo((v) => !v)}>
        {verTodo ? "Ocultar rechazados" : "Ver todo"}
      </Button>
    </Box>
  );

  if (visibles.length === 0) {
    // Un estudiante sin tema asignado solo ve el catálogo; a un guía le anticipamos el hueco.
    if (!puedeCrear) return null;
    return (
      <Seccion title="Mis temas" count={0}>
        <Vacio hint="Aún no has propuesto temas." />
      </Seccion>
    );
  }

  if (visibles.length === 1) {
    return (
      <Seccion title="Mi tema">
        <TemaTarjeta tema={visibles[0]} />
        {toggle}
      </Seccion>
    );
  }

  // Varios temas propios (p. ej. un docente): lista filtrable. Sin estado inicial: con
  // rechazados a la vista, abrir en "disponibles" dejaría la lista vacía.
  return (
    <Seccion title="Mis temas" count={visibles.length}>
      {/* `mis_` para no pisar los filtros del catálogo, que comparten la URL. */}
      <CatalogoTemas temas={visibles} estadoInicial={verTodo ? "" : "disponible"} umbralFiltros={3} claveUrl="mis_" />
      {toggle}
    </Seccion>
  );
}

function Seccion({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {title}
        {count !== undefined && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({count})
          </Typography>
        )}
      </Typography>
      {children}
    </Box>
  );
}

function Vacio({ hint }: { hint: string }) {
  return (
    <Paper elevation={0} sx={{ p: 3, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 2 }}>
      <Typography color="text.secondary" variant="body2">
        {hint}
      </Typography>
    </Paper>
  );
}
